import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import AwsS3 from '@uppy/aws-s3';
import Uppy, { UppyFile } from '@uppy/core';
import useUppyState from '@uppy/react/lib/useUppyState';
import { QueryClient } from 'react-query';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import type { UploadSession } from './upload-status-indicator';
import { GenericUppyFile, getUppyAWSS3Options, UploadType } from './uppy';
import { allowedFileTypes } from '../../../../../backend/src/matter/doc/allowed-extensions';
import { UploadFailureReason } from '../../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import { PendingFailure } from '@/api/mutations/useSendPendingFailures';
import { QueryEntity } from '@/api/types';

const BATCH_FLUSH_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB
const BATCH_FLUSH_FILE_COUNT = 5;
// How often the frontend pings /heartbeat while Uppy has files in flight.
// Backend TTL is 90s; this 30s interval gives a 3× margin so a single missed
// ping (e.g. backgrounded-tab throttling) doesn't expire the heartbeat.
const HEARTBEAT_PING_INTERVAL_MS = 30_000;

interface UseUppyUploadParams {
  matterId: string;
  allDocumentsCaseIdRef: React.MutableRefObject<string | null>;
  unpackEmailRef: React.MutableRefObject<boolean>;
  sendPendingFailures: (params: { caseId: string; uploadId: string; failures: PendingFailure[] }) => Promise<unknown>;
  refetchFiles: () => void;
  queryClient: QueryClient;
  maxFiles: number;
  onShowUploadDetails?: (uploadId: string) => void;
}

export function useUppyUpload({
  matterId,
  allDocumentsCaseIdRef,
  unpackEmailRef,
  sendPendingFailures,
  refetchFiles,
  queryClient,
  maxFiles,
  onShowUploadDetails,
}: UseUppyUploadParams) {
  const { tokens } = useAuthInfo();
  const getAccessToken = () => tokens.getAccessToken();
  const [isUploading, setIsUploading] = useState(false);
  // Once true, stays true - controls layout width expansion
  const [hasStartedUpload, setHasStartedUpload] = useState(false);

  // Per-upload session tracking
  const [uploadSessions, setUploadSessions] = useState<UploadSession[]>([]);
  const uploadSessionsRef = useRef(uploadSessions);
  uploadSessionsRef.current = uploadSessions;

  // Ref to the most recently created uploadId (used for restriction failures which fire during addFiles)
  const currentUploadIdRef = useRef<string | null>(null);

  const fileIdNameMapRef = useRef(new Map<string, string>());
  const fileIdPathMapRef = useRef(new Map<string, string>());
  const completedUploadsRef = useRef(new Set<string>());

  // Per-upload Uppy file tracking (synchronous refs for completion detection)
  // uppyFileCountRef: how many files Uppy actually accepted (totalFiles - restriction failures)
  // uppyProcessedCountRef: how many of those have finished (success + error)
  const uppyFileCountRef = useRef(new Map<string, number>());
  const uppyProcessedCountRef = useRef(new Map<string, number>());

  // Per-upload batches: files awaiting POST to backend, grouped by uploadId
  const completedBatchesByUploadRef = useRef(new Map<string, UppyFile<Record<string, unknown>, {}>[]>());
  // Per-upload heartbeat ping timers. Keep the backend's heartbeat alive while
  // Uppy has files in flight, independent of batch flushes.
  const heartbeatTimerByUploadRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // Per-upload pending failures: grouped by uploadId
  const pendingFailuresByUploadRef = useRef(new Map<string, PendingFailure[]>());

  const [uppy] = useState(() =>
    new Uppy({
      restrictions: {
        maxNumberOfFiles: maxFiles,
        allowedFileTypes,
      },

      allowMultipleUploadBatches: true,

      onBeforeFileAdded: (file) => {
        if (file.name === undefined) {
          return;
        }

        const id = uuidv4();
        const extension = file.name.split('.').length > 1 ? file.name.split('.').pop() : undefined;
        const extensionLowerCase = extension?.toLowerCase();
        const name = `${id}${extensionLowerCase ? `.${extensionLowerCase}` : ''}`;

        fileIdNameMapRef.current.set(id, file.name);

        // Capture the full path the Uppy type isn't representative of the actual type we get back
        const filePath = (file.data as File & { path?: string }).path;
        if (filePath) {
          fileIdPathMapRef.current.set(id, filePath);
        }

        // Make a new File with the correct name as S3 only cares about the File contents' name
        const newFile = new File([file.data], name, { type: file.data.type });

        return { ...file, id, data: newFile, name, meta: { ...file.meta, name } };
      },
    }).use(AwsS3, getUppyAWSS3Options(getAccessToken, matterId, UploadType.Raw)),
  );

  // Flush a specific upload's file batch to the backend
  const flushUploadBatch = useCallback(
    async (uploadId: string) => {
      const batch = completedBatchesByUploadRef.current.get(uploadId) ?? [];
      completedBatchesByUploadRef.current.set(uploadId, []);
      if (batch.length === 0) return;

      const token = await getAccessToken();
      const response = await fetch(`${APIBaseChronos}/api/matter/${matterId}/files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          files: batch.map((file) => {
            const id = file.name?.split('.')[0] ?? '';
            return {
              id,
              name: fileIdNameMapRef.current.get(id),
              size: file.size ?? 0,
              extension: file.extension,
              path: fileIdPathMapRef.current.get(id),
            };
          }),
          uploadId,
          unpackEmail: unpackEmailRef.current,
        }),
      });
      if (!response.ok) {
        console.error(`Failed to flush file batch (${response.status}): ${await response.text()}`);
      }
    },
    [matterId],
  );

  const stopHeartbeat = useCallback((uploadId: string) => {
    const timer = heartbeatTimerByUploadRef.current.get(uploadId);
    if (timer) {
      clearTimeout(timer);
      heartbeatTimerByUploadRef.current.delete(uploadId);
    }
  }, []);

  const scheduleHeartbeat = useCallback(
    (uploadId: string) => {
      if (heartbeatTimerByUploadRef.current.has(uploadId)) return;
      const tick = async () => {
        heartbeatTimerByUploadRef.current.delete(uploadId);
        try {
          const token = await getAccessToken();
          await fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/${uploadId}/heartbeat`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            keepalive: true,
          });
        } catch {
          // best-effort
        }
        // Reschedule only if the upload session is still in flight.
        const session = uploadSessionsRef.current.find((s) => s.uploadId === uploadId);
        if (!session || session.isS3Complete) return;
        const next = setTimeout(() => void tick(), HEARTBEAT_PING_INTERVAL_MS);
        heartbeatTimerByUploadRef.current.set(uploadId, next);
      };
      const initial = setTimeout(() => void tick(), HEARTBEAT_PING_INTERVAL_MS);
      heartbeatTimerByUploadRef.current.set(uploadId, initial);
    },
    [matterId],
  );

  const flushPendingFailuresForUpload = useCallback(
    async (uploadId: string) => {
      const failures = pendingFailuresByUploadRef.current.get(uploadId) ?? [];
      if (failures.length === 0 || !allDocumentsCaseIdRef.current) {
        pendingFailuresByUploadRef.current.delete(uploadId);
        return;
      }

      await sendPendingFailures({
        caseId: allDocumentsCaseIdRef.current,
        uploadId,
        failures,
      });
      pendingFailuresByUploadRef.current.delete(uploadId);
    },
    [sendPendingFailures],
  );

  // Increment processed count and finalize if all Uppy files for this upload are done
  const checkAndFinalizeUpload = useCallback((uploadId: string) => {
    const processed = (uppyProcessedCountRef.current.get(uploadId) ?? 0) + 1;
    uppyProcessedCountRef.current.set(uploadId, processed);
    const expected = uppyFileCountRef.current.get(uploadId) ?? 0;
    return expected > 0 && processed >= expected;
  }, []);

  // Finalize a single upload session when all its Uppy files are done (success + error)
  const finalizeUploadSession = useCallback(
    (uploadId: string) => {
      stopHeartbeat(uploadId);

      // Snapshot the failure counts now — flushPendingFailuresForUpload clears the ref.
      const pending = pendingFailuresByUploadRef.current.get(uploadId) ?? [];
      const skippedCount = pending.filter((f) => f.rejectedReason === UploadFailureReason.UnsupportedFileFormat).length;
      const uploadErrorCount = pending.filter((f) => f.rejectedReason === UploadFailureReason.UploadError).length;

      // Flush remaining file batch, send pending failures, then signal upload done.
      // Toast fires after the failures are persisted so 'View details' opens a modal
      // backed by complete data rather than racing the per-file POSTs.
      flushUploadBatch(uploadId).then(async () => {
        await flushPendingFailuresForUpload(uploadId);

        if (skippedCount + uploadErrorCount > 0) {
          const parts: string[] = [];
          if (skippedCount > 0) parts.push(`${skippedCount} skipped (unsupported file type)`);
          if (uploadErrorCount > 0) parts.push(`${uploadErrorCount} failed to upload`);
          toast.error(parts.join(', '), {
            action: onShowUploadDetails
              ? {
                  label: 'View details',
                  onClick: () => onShowUploadDetails(uploadId),
                }
              : undefined,
          });
        }

        const token = await getAccessToken();
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/${uploadId}/done`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          keepalive: true,
        }).catch(() => {}); // Heartbeat TTL expiry is the safety net
      });

      // Remove only this upload's files from Uppy (leave other uploads' files intact)
      const uploadFiles = uppy.getFiles().filter((f) => (f.meta as Record<string, unknown>)?.uploadId === uploadId);
      if (uploadFiles.length > 0) {
        uppy.removeFiles(uploadFiles.map((f) => f.id));
      }

      // Mark session as S3-complete
      setUploadSessions((prev) => prev.map((s) => (s.uploadId === uploadId ? { ...s, isS3Complete: true } : s)));

      // Clean up per-upload tracking refs
      uppyFileCountRef.current.delete(uploadId);
      uppyProcessedCountRef.current.delete(uploadId);

      // Invalidate so the processing status appears
      queryClient.invalidateQueries([QueryEntity.UploadHistory]);
    },
    [flushUploadBatch, flushPendingFailuresForUpload, matterId, onShowUploadDetails, queryClient, stopHeartbeat, uppy],
  );

  const handleUppyUploadSuccess = useCallback(
    (file: UppyFile<Record<string, unknown>, {}> | undefined) => {
      if (!file || completedUploadsRef.current.has(file.id)) return;
      completedUploadsRef.current.add(file.id);

      const uploadId = file.meta?.uploadId as string | undefined;
      if (!uploadId) return;

      // Add to per-upload batch
      const batch = completedBatchesByUploadRef.current.get(uploadId) ?? [];
      batch.push(file);
      completedBatchesByUploadRef.current.set(uploadId, batch);

      setUploadSessions((prev) =>
        prev.map((s) => (s.uploadId === uploadId ? { ...s, completedFiles: s.completedFiles + 1 } : s)),
      );

      if (checkAndFinalizeUpload(uploadId)) {
        finalizeUploadSession(uploadId);
      } else {
        // Flush when accumulated batch exceeds either threshold (size or file count)
        const batchBytes = batch.reduce((sum, f) => sum + (f.size ?? 0), 0);
        if (batchBytes >= BATCH_FLUSH_SIZE_BYTES || batch.length >= BATCH_FLUSH_FILE_COUNT) {
          flushUploadBatch(uploadId);
        }
      }
    },
    [flushUploadBatch, finalizeUploadSession, checkAndFinalizeUpload],
  );

  const handleUppyUploadError = useCallback(
    (file: UppyFile<Record<string, unknown>, {}> | undefined, error: Error) => {
      const uploadId = file?.meta?.uploadId as string | undefined;
      const id = file?.name?.split('.')[0] ?? '';
      const filename = fileIdNameMapRef.current.get(id) ?? file?.name ?? 'Unknown file';
      const extension = file?.name?.split('.').pop() ?? '';

      if (uploadId) {
        setUploadSessions((prev) =>
          prev.map((s) =>
            s.uploadId === uploadId
              ? {
                  ...s,
                  failedFiles: s.failedFiles + 1,
                  failures: [...s.failures, { filename, reason: 'Failed', type: 'failed' as const }],
                }
              : s,
          ),
        );

        const failures = pendingFailuresByUploadRef.current.get(uploadId) ?? [];
        failures.push({ filename, extension, rejectedReason: UploadFailureReason.UploadError });
        pendingFailuresByUploadRef.current.set(uploadId, failures);

        if (checkAndFinalizeUpload(uploadId)) {
          finalizeUploadSession(uploadId);
        }
      }

      toast.error(`Failed to upload file: ${error?.message || 'unknown error'}. Please check your network connection.`);
      console.error(`Upload failed for ${filename}:`, error);
    },
    [finalizeUploadSession, checkAndFinalizeUpload],
  );

  // Fires synchronously inside uppy.addFiles. Just buffer the failure into the
  // pending ref — onUploadUppy reads counts from the ref and updates state in
  // one batch after addFiles returns, avoiding N synchronous re-renders.
  const handleUppyRestrictionFailed = useCallback((file: GenericUppyFile | undefined) => {
    if (!file?.id && !file?.name) return;

    const id = file.name?.split('.')[0] ?? '';
    const extension = file.name?.split('.').pop() || '';
    const filename = fileIdNameMapRef.current.get(id) || file.name || '';

    const uploadId = currentUploadIdRef.current;
    if (!uploadId) return;

    const failures = pendingFailuresByUploadRef.current.get(uploadId) ?? [];
    failures.push({
      filename,
      extension,
      rejectedReason: UploadFailureReason.UnsupportedFileFormat,
    });
    pendingFailuresByUploadRef.current.set(uploadId, failures);
  }, []);

  // Register Uppy event listeners
  useEffect(() => {
    uppy.off('upload-success', handleUppyUploadSuccess);
    uppy.off('upload-error', handleUppyUploadError);
    uppy.off('restriction-failed', handleUppyRestrictionFailed);

    uppy.on('upload-success', handleUppyUploadSuccess);
    uppy.on('upload-error', handleUppyUploadError);
    uppy.on('restriction-failed', handleUppyRestrictionFailed);

    return () => {
      uppy.off('upload-success', handleUppyUploadSuccess);
      uppy.off('upload-error', handleUppyUploadError);
      uppy.off('restriction-failed', handleUppyRestrictionFailed);
    };
  }, [handleUppyUploadSuccess, handleUppyUploadError, handleUppyRestrictionFailed, uppy]);

  const onUploadUppy = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const uploadId = uuidv4();
      currentUploadIdRef.current = uploadId;

      uppy.addFiles(
        files.map((file) => ({
          source: 'local',
          data: file,
          name: file.name,
          meta: { uploadId },
        })),
      );

      // Count files Uppy actually accepted (excludes restriction failures like bad extensions)
      const uppyFiles = uppy
        .getFiles()
        .filter((f) => (f.meta as Record<string, unknown>)?.uploadId === uploadId).length;
      uppyFileCountRef.current.set(uploadId, uppyFiles);
      uppyProcessedCountRef.current.set(uploadId, 0);

      // restriction-failed fired synchronously above for each rejected file —
      // commit them all in a single state update.
      const skipped = pendingFailuresByUploadRef.current.get(uploadId) ?? [];
      setUploadSessions((prev) => [
        ...prev,
        {
          uploadId,
          totalFiles: uppyFiles + skipped.length,
          completedFiles: 0,
          failedFiles: skipped.length,
          failures: skipped.map((f) => ({
            filename: f.filename,
            reason: 'Skipped',
            type: 'skipped' as const,
          })),
          isS3Complete: false,
        },
      ]);

      if (uppyFiles === 0) {
        // All files were rejected by restrictions — finalize immediately
        finalizeUploadSession(uploadId);
      } else {
        uppy.upload();
        setIsUploading(true);
        // Keep the backend heartbeat alive while files are in flight, even if
        // the current batch is empty between waves.
        scheduleHeartbeat(uploadId);
      }

      setHasStartedUpload(true);
    },
    [uppy, finalizeUploadSession, scheduleHeartbeat],
  );

  const uppyProgressByUpload = useUppyState(uppy, (state) => {
    const progressMap: Record<string, number> = {};
    const files = Object.values(state.files);

    const uploadGroups = new Map<string, { bytesUploaded: number; bytesTotal: number }>();

    for (const file of files) {
      const uploadId = (file.meta as Record<string, unknown>)?.uploadId as string | undefined;
      if (!uploadId) continue;

      const group = uploadGroups.get(uploadId) ?? { bytesUploaded: 0, bytesTotal: 0 };
      group.bytesUploaded += Number(file.progress?.bytesUploaded) || 0;
      group.bytesTotal += Number(file.progress?.bytesTotal) || 0;
      uploadGroups.set(uploadId, group);
    }

    for (const [uploadId, group] of uploadGroups) {
      progressMap[uploadId] = group.bytesTotal > 0 ? Math.round((group.bytesUploaded / group.bytesTotal) * 100) : 0;
    }

    return progressMap;
  });

  // When all Uppy files fail, Uppy may not fire the 'complete' event.
  // Detect when all sessions are S3-complete and clean up Uppy state.
  useEffect(() => {
    if (!isUploading) return;
    if (uploadSessions.length === 0) return;
    if (uploadSessions.some((s) => !s.isS3Complete)) return;

    // All sessions are S3-complete — clean up Uppy
    uppy.resetProgress();
    uppy.removeFiles(uppy.getFiles().map((f) => f.id));
    completedUploadsRef.current.clear();
    fileIdNameMapRef.current.clear();
    fileIdPathMapRef.current.clear();
    setIsUploading(false);
    currentUploadIdRef.current = null;
  }, [isUploading, uploadSessions, uppy]);

  // Callback for UploadStatusItem to trigger when processing completes or fails.
  // Don't remove the session — let the status row stay visible so the user sees the result.
  // The row auto-dismisses after a delay via UploadStatusList's dismissUpload.
  const handleProcessingComplete = useCallback(
    (_uploadId?: string) => {
      refetchFiles();
      queryClient.invalidateQueries([QueryEntity.UploadHistory]);
    },
    [refetchFiles, queryClient],
  );

  return {
    onUploadUppy,
    isUploading,
    setIsUploading,
    hasStartedUpload,
    setHasStartedUpload,
    uploadSessions,
    uppyProgressByUpload,
    handleProcessingComplete,
    uploadSessionsRef,
  };
}
